import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  Timestamp,
  writeBatch,
} from "firebase/firestore";
import { getDb } from "./config";
import {
  PageData,
  PageListItem,
  Template,
  Site,
  SiteListItem,
  CreateSiteData,
  DomainConfig,
  ThemeSettings,
} from "../types";
import { replaceClinicInfoPlaceholders } from "../config/clinicPlaceholders";
import { API_ENDPOINTS } from "../config/apiConfig";

const COLLECTION_SITES = "clinic-cms-sites";
const COLLECTION_TEMPLATES = "clinic-cms-templates";

// ============ SITES ============

export const getAllSites = async (divisionId: number): Promise<SiteListItem[]> => {
  const db = getDb();
  const q = query(
    collection(db, COLLECTION_SITES),
    where("divisionId", "==", divisionId),
    orderBy("updatedAt", "desc")
  );
  const snapshot = await getDocs(q);

  const sites: SiteListItem[] = [];

  for (const docSnap of snapshot.docs) {
    const data = docSnap.data();

    // Get page count for this site
    const pagesSnapshot = await getDocs(collection(db, COLLECTION_SITES, docSnap.id, "pages"));

    sites.push({
      id: docSnap.id,
      divisionId: data.divisionId,
      name: data.name,
      slug: data.slug,
      logo: data.logo,
      theme: data.theme || { primaryColor: "#3b82f6", secondaryColor: "#10b981" },
      updatedAt: data.updatedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      pageCount: pagesSnapshot.size,
      isPublished: data.isPublished || false,
    });
  }

  return sites;
};

export const getSiteById = async (id: string): Promise<Site | null> => {
  const db = getDb();
  const docRef = doc(db, COLLECTION_SITES, id);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  const data = docSnap.data();
  return {
    id: docSnap.id,
    divisionId: data.divisionId,
    name: data.name,
    slug: data.slug,
    phone: data.phone,
    email: data.email,
    address: data.address,
    lineId: data.lineId,
    logo: data.logo,
    favicon: data.favicon,
    gaMeasurementId: data.gaMeasurementId,
    theme: data.theme || { primaryColor: "#3b82f6", secondaryColor: "#10b981" },
    templateId: data.templateId,
    domain: data.domain || undefined,
    isPublished: data.isPublished || false,
    publishedAt: data.publishedAt || undefined,
    createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
    updatedAt: data.updatedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
  };
};

export const createSite = async (data: CreateSiteData): Promise<Site> => {
  const db = getDb();
  const id = `site-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  const now = Timestamp.now();

  const siteData = {
    divisionId: data.divisionId,
    name: data.name,
    slug: data.slug,
    phone: data.phone || "",
    logo: data.logo || "",
    theme: data.theme,
    templateId: data.templateId,
    createdAt: now,
    updatedAt: now,
  };

  await setDoc(doc(db, COLLECTION_SITES, id), siteData);

  return {
    id,
    divisionId: siteData.divisionId,
    name: siteData.name,
    slug: siteData.slug,
    phone: siteData.phone,
    logo: siteData.logo,
    theme: siteData.theme,
    templateId: siteData.templateId,
    createdAt: now.toDate().toISOString(),
    updatedAt: now.toDate().toISOString(),
  };
};

export const updateSite = async (id: string, updates: Partial<Site>): Promise<void> => {
  const db = getDb();
  const docRef = doc(db, COLLECTION_SITES, id);

  const { id: _, createdAt: __, ...updateData } = updates;
  await updateDoc(docRef, {
    ...updateData,
    updatedAt: Timestamp.now(),
  });
};

export const deleteSite = async (id: string): Promise<void> => {
  const db = getDb();
  const batch = writeBatch(db);

  // Delete all pages in the site first
  const pagesSnapshot = await getDocs(collection(db, COLLECTION_SITES, id, "pages"));
  pagesSnapshot.docs.forEach((pageDoc) => {
    batch.delete(pageDoc.ref);
  });

  // Delete the site document
  batch.delete(doc(db, COLLECTION_SITES, id));

  await batch.commit();
};

// ============ PAGES (with siteId) ============

export const getAllPages = async (siteId: string): Promise<PageListItem[]> => {
  const db = getDb();
  const snapshot = await getDocs(collection(db, COLLECTION_SITES, siteId, "pages"));

  const pages = snapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      name: data.name,
      slug: data.slug,
      updatedAt: data.updatedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      isPublished: data.isPublished || false,
      order: data.order ?? 999, // Default high order for pages without order
    };
  });

  // Sort by order (ascending), then by updatedAt (descending) for same order
  return pages.sort((a, b) => {
    if (a.order !== b.order) return a.order - b.order;
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });
};

export const getPageById = async (siteId: string, pageId: string): Promise<PageData | null> => {
  const db = getDb();
  const docRef = doc(db, COLLECTION_SITES, siteId, "pages", pageId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  const data = docSnap.data();
  return {
    id: docSnap.id,
    name: data.name,
    slug: data.slug,
    content: data.content || "",
    createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
    updatedAt: data.updatedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
    isPublished: data.isPublished || false,
    seo: data.seo,
    theme: data.theme,
  };
};

export const createPage = async (
  siteId: string,
  name: string,
  slug: string,
  content: string = "",
  theme?: ThemeSettings
): Promise<PageData> => {
  const db = getDb();
  const id = `page-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  const now = Timestamp.now();

  // Get current max order
  const existingPages = await getAllPages(siteId);
  const maxOrder = existingPages.reduce((max, p) => Math.max(max, p.order ?? 0), -1);

  const pageData: Record<string, unknown> = {
    name,
    slug,
    content,
    createdAt: now,
    updatedAt: now,
    isPublished: true, // Default to Active
    order: maxOrder + 1, // Add at the end
  };

  // Add theme if provided
  if (theme) {
    pageData.theme = theme;
  }

  await setDoc(doc(db, COLLECTION_SITES, siteId, "pages", id), pageData);

  // Update site's updatedAt
  await updateDoc(doc(db, COLLECTION_SITES, siteId), {
    updatedAt: now,
  });

  return {
    id,
    name,
    slug,
    content,
    theme,
    order: maxOrder + 1,
    createdAt: now.toDate().toISOString(),
    updatedAt: now.toDate().toISOString(),
    isPublished: true,
  };
};

export const updatePage = async (siteId: string, pageId: string, updates: Partial<PageData>): Promise<void> => {
  const db = getDb();
  const docRef = doc(db, COLLECTION_SITES, siteId, "pages", pageId);

  const { id: _, ...updateData } = updates;
  const now = Timestamp.now();

  await updateDoc(docRef, {
    ...updateData,
    updatedAt: now,
  });

  // Update site's updatedAt
  await updateDoc(doc(db, COLLECTION_SITES, siteId), {
    updatedAt: now,
  });
};

export const deletePage = async (siteId: string, pageId: string): Promise<void> => {
  const db = getDb();
  await deleteDoc(doc(db, COLLECTION_SITES, siteId, "pages", pageId));

  // Update site's updatedAt
  await updateDoc(doc(db, COLLECTION_SITES, siteId), {
    updatedAt: Timestamp.now(),
  });
};

export const savePageContent = async (siteId: string, pageId: string, content: string): Promise<void> => {
  await updatePage(siteId, pageId, { content });
};

export const duplicatePage = async (siteId: string, pageId: string): Promise<PageData> => {
  const db = getDb();

  // Get the original page
  const originalPage = await getPageById(siteId, pageId);
  if (!originalPage) {
    throw new Error("Page not found");
  }

  // Generate new name and slug
  const newName = `${originalPage.name} (Copy)`;
  const baseSlug = `${originalPage.slug}-copy`;

  // Check if slug exists and make it unique
  const existingPages = await getAllPages(siteId);
  const existingSlugs = new Set(existingPages.map(p => p.slug));

  let newSlug = baseSlug;
  let slugCounter = 1;
  while (existingSlugs.has(newSlug)) {
    slugCounter++;
    newSlug = `${baseSlug}-${slugCounter}`;
  }

  // Get max order for new page
  const maxOrder = existingPages.reduce((max, p) => Math.max(max, p.order ?? 0), -1);

  const id = `page-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  const now = Timestamp.now();

  const pageData: Record<string, unknown> = {
    name: newName,
    slug: newSlug,
    content: originalPage.content,
    createdAt: now,
    updatedAt: now,
    isPublished: false, // Duplicated pages start as inactive
    order: maxOrder + 1,
  };

  // Copy SEO if exists
  if (originalPage.seo) {
    pageData.seo = { ...originalPage.seo };
  }

  // Copy theme if exists
  if (originalPage.theme) {
    pageData.theme = { ...originalPage.theme };
  }

  await setDoc(doc(db, COLLECTION_SITES, siteId, "pages", id), pageData);

  // Update site's updatedAt
  await updateDoc(doc(db, COLLECTION_SITES, siteId), {
    updatedAt: now,
  });

  return {
    id,
    name: newName,
    slug: newSlug,
    content: originalPage.content,
    seo: originalPage.seo,
    theme: originalPage.theme,
    order: maxOrder + 1,
    createdAt: now.toDate().toISOString(),
    updatedAt: now.toDate().toISOString(),
    isPublished: false,
  };
};

export const reorderPages = async (siteId: string, pageIds: string[]): Promise<void> => {
  const db = getDb();
  const batch = writeBatch(db);
  const now = Timestamp.now();

  // Update order for each page
  pageIds.forEach((pageId, index) => {
    const pageRef = doc(db, COLLECTION_SITES, siteId, "pages", pageId);
    batch.update(pageRef, { order: index });
  });

  // Update site's updatedAt
  batch.update(doc(db, COLLECTION_SITES, siteId), { updatedAt: now });

  await batch.commit();
};

// ============ TEMPLATES ============

export const getAllTemplates = async (): Promise<Template[]> => {
  const db = getDb();
  const snapshot = await getDocs(collection(db, COLLECTION_TEMPLATES));

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Template[];
};

export const getTemplateById = async (id: string): Promise<Template | null> => {
  const db = getDb();
  const docRef = doc(db, COLLECTION_TEMPLATES, id);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return {
    id: docSnap.id,
    ...docSnap.data(),
  } as Template;
};

export const initializeTemplates = async (templates: Template[]): Promise<void> => {
  const db = getDb();

  for (const template of templates) {
    const docRef = doc(db, COLLECTION_TEMPLATES, template.id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      await setDoc(docRef, template);
    }
  }
};

// Sync templates - always overwrite existing
export const syncTemplates = async (templates: Template[]): Promise<{ synced: number; errors: string[] }> => {
  const db = getDb();
  let synced = 0;
  const errors: string[] = [];

  for (const template of templates) {
    try {
      const docRef = doc(db, COLLECTION_TEMPLATES, template.id);
      await setDoc(docRef, template);
      synced++;
    } catch (error) {
      errors.push(`Failed to sync ${template.id}: ${error}`);
    }
  }

  return { synced, errors };
};

// ============ SITE GENERATION (from template) ============

// Default blank page content - simple structure with header and content area
const getDefaultPageContent = () => {
  return JSON.stringify({
    ROOT: {
      type: { resolvedName: "RootContainer" },
      isCanvas: true,
      props: {},
      displayName: "RootContainer",
      custom: {},
      hidden: false,
      nodes: [],
      linkedNodes: {},
    },
  });
};

export const generateSiteFromTemplate = async (
  siteId: string,
  templateId: string,
  clinicInfo: { name: string; phone: string; email?: string; address?: string; lineId?: string; logo?: string },
  theme?: ThemeSettings
): Promise<{ success: boolean; pages: PageData[] }> => {
  // If blank template, create a default blank home page
  if (templateId === "blank") {
    const homePage = await createPage(siteId, "Home", "home", getDefaultPageContent(), theme);
    return { success: true, pages: [homePage] };
  }

  // Fetch all templates from Firestore
  let templates = await getAllTemplates();

  // If no templates in Firestore, try to fetch from API and sync
  if (templates.length === 0) {
    console.log("No templates in Firestore, fetching from API...");
    try {
      const response = await fetch(API_ENDPOINTS.templatesData);
      if (response.ok) {
        const data = await response.json();
        if (data.templates && data.templates.length > 0) {
          // Sync templates to Firestore
          await syncTemplates(data.templates);
          templates = data.templates;
          console.log(`Synced ${templates.length} templates from API`);
        }
      }
    } catch (err) {
      console.warn("Could not fetch templates from API:", err);
    }
  }

  // templateId can be a theme name like 'beauty-clinic' or full id like 'template-beauty-clinic'
  const selectedTheme = templateId.replace("template-", "");

  // Page configurations to create
  const pageConfigs = [
    { name: "Home", slug: "home", category: "home" },
    { name: "About", slug: "about", category: "about" },
    { name: "Services", slug: "services", category: "services" },
    { name: "Doctors", slug: "doctors", category: "doctors" },
    { name: "Contact", slug: "contact", category: "contact" },
  ];

  const createdPages: PageData[] = [];

  for (const config of pageConfigs) {
    // Find template for this page - first try theme-specific, then generic
    let template = templates.find((t) => t.theme === selectedTheme && t.category === config.category);

    if (!template) {
      template = templates.find((t) => t.category === config.category && !t.theme);
    }

    if (!template) {
      template = templates.find((t) => t.category === config.category);
    }

    let content: string;
    if (template && template.content) {
      // Use template content and replace clinic info
      content = replaceClinicInfoPlaceholders(template.content, clinicInfo);
    } else {
      // Fallback to empty page if no template found
      console.warn(`No template found for ${config.category}, creating empty page`);
      content = getDefaultPageContent();
    }

    const createdPage = await createPage(siteId, config.name, config.slug, content, theme);
    createdPages.push(createdPage);
  }

  return { success: true, pages: createdPages };
};

// ============ DOMAIN MANAGEMENT ============

export const updateSiteDomain = async (siteId: string, domainConfig: DomainConfig): Promise<void> => {
  const db = getDb();
  const docRef = doc(db, COLLECTION_SITES, siteId);

  await updateDoc(docRef, {
    domain: domainConfig,
    updatedAt: Timestamp.now(),
  });
};

export const verifySiteDomain = async (siteId: string, verified: boolean): Promise<void> => {
  const db = getDb();
  const docRef = doc(db, COLLECTION_SITES, siteId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    throw new Error("Site not found");
  }

  const currentDomain = docSnap.data().domain;
  if (!currentDomain?.customDomain) {
    throw new Error("No domain configured");
  }

  const now = Timestamp.now();
  await updateDoc(docRef, {
    domain: {
      ...currentDomain,
      verified,
      verifiedAt: verified ? now.toDate().toISOString() : null,
      lastCheckedAt: now.toDate().toISOString(),
    },
    updatedAt: now,
  });
};

export const removeSiteDomain = async (siteId: string): Promise<void> => {
  const db = getDb();
  const docRef = doc(db, COLLECTION_SITES, siteId);

  await updateDoc(docRef, {
    domain: null,
    updatedAt: Timestamp.now(),
  });
};

// ============ SITE PUBLISH ============

export const publishSite = async (siteId: string): Promise<void> => {
  const db = getDb();
  const docRef = doc(db, COLLECTION_SITES, siteId);
  const now = Timestamp.now();

  await updateDoc(docRef, {
    isPublished: true,
    publishedAt: now.toDate().toISOString(),
    updatedAt: now,
  });
};

export const unpublishSite = async (siteId: string): Promise<void> => {
  const db = getDb();
  const docRef = doc(db, COLLECTION_SITES, siteId);

  await updateDoc(docRef, {
    isPublished: false,
    updatedAt: Timestamp.now(),
  });
};
